import { ApiService } from './api';

// Auth API service instance for OAuth endpoints
const authApiService = new ApiService();
authApiService['baseURL'] = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1';

// Social automation API service instance for demographics
const socialApiService = new ApiService();
socialApiService['baseURL'] = import.meta.env.VITE_SCRAPING_API_URL || 'http://localhost:3003/api/v1';

export interface InstagramConnection {
  id: string;
  provider: string;
  providerUsername: string;
  linkedPlatformAccountUsername: string | null;
  providerUserId: string;
  tokenStatus: 'active' | 'expiring_soon' | 'expired';
  tokenExpiresAt: string;
  linkedPlatformAccountId: string | null;
  linkedPlatformAccountPartitionKey: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConnectionStatusResponse {
  success: boolean;
  data: InstagramConnection[];
  error?: { message: string };
}

export interface AuthorizeResponse {
  success: boolean;
  data: {
    authorizeUrl: string;
  };
  error?: { message: string };
}

export interface DisconnectResponse {
  success: boolean;
  message?: string;
  error?: { message: string };
}

export interface DemographicsData {
  [type: string]: {
    data: Record<string, number>;
    fetchedAt: string;
  };
}

export interface DemographicsResponse {
  success: boolean;
  data: {
    platformAccountId: string;
    demographics: DemographicsData;
    hasData: boolean;
  };
  error?: { message: string };
}

class InstagramConnectionService {
  /**
   * Get the OAuth authorization URL to start the Instagram connection flow
   */
  async getAuthorizationUrl(
    organizationId: string,
    customerId: string,
    platformAccountId: string,
    partitionKey: number,
    fbPageId?: string
  ): Promise<AuthorizeResponse> {
    const params: Record<string, string> = {
      organizationId,
      customerId,
      platformAccountId,
      partitionKey: String(partitionKey),
    };
    if (fbPageId) params.fbPageId = fbPageId;
    return authApiService.get<AuthorizeResponse>('/oauth/instagram/authorize', params);
  }

  /**
   * Get all Instagram connections for an organization
   */
  async getConnectionStatus(organizationId: string): Promise<ConnectionStatusResponse> {
    return authApiService.get<ConnectionStatusResponse>('/oauth/instagram/status', {
      organizationId,
    });
  }

  /**
   * Exchange OAuth callback code for tokens (completes the connection)
   */
  async exchangeCode(code: string, state: string): Promise<{ success: boolean; data?: any; error?: { message: string } }> {
    return authApiService.post('/oauth/instagram/callback', { code, state });
  }

  /**
   * Disconnect an Instagram account (revoke OAuth)
   */
  async disconnect(oauthAccountId: string): Promise<DisconnectResponse> {
    return authApiService.delete<DisconnectResponse>(`/oauth/instagram/${oauthAccountId}`);
  }

  /**
   * Get audience demographics for a connected Instagram account
   */
  async getDemographics(
    customerId: string,
    platformAccountId: string,
    partitionKey: number
  ): Promise<DemographicsResponse> {
    return socialApiService.get<DemographicsResponse>('/instagram/demographics', {
      customerId,
      platformAccountId,
      partitionKey: String(partitionKey),
    });
  }
}

export const instagramConnectionService = new InstagramConnectionService();
